import { useState, useRef, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { toast, Toaster } from "sonner";
import { api, resolveImg } from "@/lib/api";
import {
  ArrowUpRight, ArrowDown, ArrowRight, Phone, Instagram, Mail, Menu, X,
  ShieldCheck, Lightbulb, Layers, HeartHandshake, Check,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMG = {
  hero: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/2a8bdb836cb2aa941ff9821a12ffaaf5890721dfaf9e383d105f4191ee85eb21.jpeg",
  heroCap: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/f94d7aab5349a78b98cdcc4c9e61acc52e20f79a18df930817474ab94a4742fe.jpeg",
  slideTshirt: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/a993799de83657291ed10894ac3c9085cda69a928bb7ff83a570b63f8bf5d0cd.jpeg",
  slideCap: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/45b7a46f6ce5baf5121d4bb9df408bf2198f014ec44abe6e8213d492c48153f8.jpeg",
  slidePolo: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/1ba903a6a8e575e07c37a2403bd2d0a993d7ff63b2d89f65220df0f3782b1b30.jpeg",
  slideApron: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/99cf20b324b6b8e19a8ee09a6cb934605e4eae386d34a7bc8e75608b7006cf9b.jpeg",
  slideMug: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/e45ba96e4875af2aea3ce31deac1eae8456181af89f3f8dba63238c9e5e690a6.jpeg",
  apparel: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/605ad99cca3584bc1202cb07a20ad8dd934dce8d88cb2469ddaef7f158b7113e.jpeg",
  merch: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/73c321244d798687190de3741a29087d25871eb4d015605958aaf28271888a48.jpeg",
  custom: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/dd5464fabd3fbdf5545a25e91503666a99d483550c22f9c63541823d6eaf5d9a.jpeg",
  project: "https://static.prod-images.emergentagent.com/jobs/d0d8c1dc-80cf-41f7-8985-ae941c398f01/images/d857fb92d59f14017d2f54394267af94211970ff0d3891922b3631b800e9b6a0.jpeg",
  logo: "https://customer-assets-m6fa6gv7.emergentagent.net/job_dystopica-corporate/artifacts/irzmlhig_solo%20dist%C3%B3pica%20en%20negro.png",
  clientPNA: "https://customer-assets-m6fa6gv7.emergentagent.net/job_dystopica-corporate/artifacts/dsjghwck_logo2%20%283%29.webp",
  clientTCCAR: "https://customer-assets-m6fa6gv7.emergentagent.net/job_dystopica-corporate/artifacts/3wp4h6fx_LOGO.jpeg",
};

const ease = [0.22, 1, 0.36, 1];

/* ---------- Reusable reveal ---------- */
const LineReveal = ({ children, delay = 0, className = "" }) => (
  <span className="reveal-mask">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, ease, delay }}
    >
      {children}
    </motion.span>
  </span>
);

const FadeUp = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.7, ease, delay }}
  >
    {children}
  </motion.div>
);

/* ---------- Navigation ---------- */
const NAV_LINKS = [
  { id: "servicios", label: "Servicios" },
  { id: "proceso", label: "Proceso" },
  { id: "proyectos", label: "Proyectos" },
  { id: "contacto", label: "Contacto" },
];

const Nav = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const go = (id) => {
    setOpen(false);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const goBlog = () => {
    setOpen(false);
    navigate("/blog");
  };
  return (
    <header
      data-testid="site-nav"
      className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-black/60 border-b border-white/10"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="font-display text-2xl tracking-tight leading-none"
        >
          DISTÓPICA<span style={{ color: "var(--fuchsia)" }}>.</span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-[var(--yellow)] transition-colors"
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-link-blog"
            onClick={goBlog}
            className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-[var(--yellow)] transition-colors"
          >
            Blog
          </button>
          <button
            data-testid="nav-cta"
            onClick={() => go("contacto")}
            className="font-mono-x text-xs uppercase tracking-[0.2em] bg-[var(--yellow)] text-black px-4 py-2 rounded-sm hover:bg-white transition-colors"
          >
            Cotizar
          </button>
        </nav>

        <button
          data-testid="nav-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen((v) => !v)}
          aria-label="Menú"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="md:hidden overflow-hidden border-t border-white/10 bg-black/90"
          data-testid="mobile-menu"
        >
          <div className="px-5 py-4 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`mobile-link-${l.id}`}
                onClick={() => go(l.id)}
                className="font-mono-x text-sm uppercase tracking-[0.2em] text-left text-neutral-200"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-link-blog"
              onClick={goBlog}
              className="font-mono-x text-sm uppercase tracking-[0.2em] text-left text-neutral-200"
            >
              Blog
            </button>
          </div>
        </motion.div>
      )}
    </header>
  );
};

/* ---------- Hero carousel ---------- */
const HERO_SLIDES = [
  { img: IMG.slideTshirt, label: "Remeras" },
  { img: IMG.slideCap, label: "Gorras" },
  { img: IMG.slidePolo, label: "Chombas" },
  { img: IMG.slideApron, label: "Delantales" },
  { img: IMG.slideMug, label: "Tazas" },
];

const HeroCarousel = ({ className = "", fill = false }) => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % HERO_SLIDES.length), 3500);
    return () => clearInterval(t);
  }, []);
  const slide = HERO_SLIDES[i];
  return (
    <div className={`relative ${fill ? "h-full" : ""} ${className}`} data-testid="hero-carousel">
      <div className={`relative overflow-hidden bg-[#0A0A0A] ${fill ? "h-full border-l border-white/15" : "border border-white/15 aspect-[3/4]"}`}>
        <AnimatePresence>
          <motion.img
            key={slide.img}
            src={slide.img}
            alt={`${slide.label} personalizadas Distópica`}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.9, ease }}
            className="absolute inset-0 w-full h-full object-cover"
          />
        </AnimatePresence>
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 via-transparent to-transparent pointer-events-none" />
        <span className="patch text-[var(--yellow)] bg-black/70 backdrop-blur absolute top-6 left-6 z-10">{slide.label}</span>
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
          {HERO_SLIDES.map((s, idx) => (
            <button
              key={idx}
              onClick={() => setI(idx)}
              data-testid={`carousel-dot-${idx}`}
              aria-label={`Ver ${s.label}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${idx === i ? "w-6 bg-[var(--fuchsia)]" : "w-1.5 bg-white/40 hover:bg-white/70"}`}
            />
          ))}
        </div>
      </div>
      {!fill && <span className="absolute -bottom-2 -right-2 w-16 h-16 border-b-2 border-r-2 border-[var(--fuchsia)] pointer-events-none" />}
    </div>
  );
};

/* ---------- Hero ---------- */
const Hero = () => {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="hero" className="relative min-h-screen bg-[#0A0A0A] overflow-hidden pt-16">
      {/* full-height product carousel (right side, desktop) */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, ease, delay: 0.4 }}
        className="absolute right-0 top-0 bottom-0 w-[44%] xl:w-[42%] hidden lg:block z-0"
        data-testid="hero-product"
      >
        <HeroCarousel fill className="h-full" />
      </motion.div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col justify-center min-h-[calc(100vh-4rem)] py-20 lg:py-16 lg:pr-[42%]">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <span className="patch text-[var(--yellow)]" data-testid="hero-tag">
            Indumentaria + Merch corporativo
          </span>
        </motion.div>

        <h1 className="font-display uppercase leading-[1.02] tracking-tight text-[12vw] sm:text-[9vw] lg:text-[4.6vw] max-w-[16ch]">
          <LineReveal delay={0.15}>Hacemos que</LineReveal>
          <LineReveal delay={0.25}>tu marca</LineReveal>
          <LineReveal delay={0.35}>
            <span className="text-stroke">se vea,</span>
          </LineReveal>
          <LineReveal delay={0.45}>se use</LineReveal>
          <LineReveal delay={0.55}>
            y se recuerde<span style={{ color: "var(--fuchsia)" }}>.</span>
          </LineReveal>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.7, ease }}
          className="mt-8 max-w-xl text-base sm:text-lg text-neutral-300"
          data-testid="hero-subtitle"
        >
          Creamos indumentaria y merchandising personalizados para que tu marca esté presente en cada detalle.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.85, duration: 0.7, ease }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => go("contacto")}
            className="group inline-flex items-center gap-2 bg-[var(--fuchsia)] text-white font-mono-x uppercase text-sm tracking-[0.15em] px-7 py-4 rounded-sm hover:bg-white hover:text-black transition-colors"
          >
            Cotizar proyecto
            <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => go("servicios")}
            className="inline-flex items-center gap-2 border border-white/30 text-white font-mono-x uppercase text-sm tracking-[0.15em] px-7 py-4 rounded-sm hover:border-[var(--yellow)] hover:text-[var(--yellow)] transition-colors"
          >
            Ver servicios
            <ArrowDown size={18} />
          </button>
        </motion.div>

        {/* mobile carousel */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1, duration: 0.7, ease }}
          className="lg:hidden mt-14 max-w-sm"
        >
          <HeroCarousel />
        </motion.div>
      </div>
    </section>
  );
};

/* ---------- Marquee ribbon ---------- */
const Ribbon = () => (
  <div className="bg-[var(--yellow)] text-black py-4 border-y-2 border-black" data-testid="marquee-ribbon">
    <Marquee speed={45} gradient={false}>
      {Array.from({ length: 6 }).map((_, i) => (
        <span key={i} className="font-display uppercase text-3xl sm:text-4xl tracking-tight mx-8 flex items-center gap-8">
          Distópica <span className="text-[var(--fuchsia)]">✳</span> Merch Corporativo <span className="text-[var(--fuchsia)]">✳</span> DTF & Bordado <span className="text-[var(--fuchsia)]">✳</span>
        </span>
      ))}
    </Marquee>
  </div>
);

/* ---------- Manifesto (light) ---------- */
const Manifesto = () => (
  <section className="bg-[var(--bg-light)] text-black py-24 sm:py-36" data-testid="manifesto">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-4">
        <FadeUp>
          <span className="patch text-black -rotate-2">01 / Identidad</span>
        </FadeUp>
      </div>
      <div className="lg:col-span-8">
        <FadeUp delay={0.1}>
          <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-6xl lg:text-7xl mb-10">
            La marca también comunica <span className="text-stroke-dark">cuando se viste</span>
          </h2>
        </FadeUp>
        <FadeUp delay={0.2}>
          <p className="text-lg sm:text-xl leading-relaxed text-neutral-800 max-w-3xl">
            Cada persona de tu equipo representa a tu empresa. Ya sea en la oficina, un evento o
            una visita a un cliente, la indumentaria y el merchandising comunican quién eres y
            fortalecen la presencia de tu marca. En Distópica no solo personalizamos prendas y
            productos: te ayudamos a convertirlos en una extensión de tu identidad, usando
            materiales de calidad y técnicas como <strong>DTF</strong> y <strong>bordado</strong>.
            Además te asesoramos en cada etapa para que cada elección proyecte una imagen
            profesional, coherente y memorable.
          </p>
        </FadeUp>
      </div>
    </div>
  </section>
);

/* ---------- Services ---------- */
const SERVICES = [
  {
    tag: "Indumentaria corporativa",
    img: IMG.apparel,
    items: ["Remeras", "Chombas", "Buzos", "Camperas", "Gorras"],
    num: "01",
  },
  {
    tag: "Merchandising",
    img: IMG.merch,
    items: ["Tazas", "Botellas", "Cuadernos", "Agendas", "Bolsos", "Kits corporativos"],
    num: "02",
  },
  {
    tag: "Personalización",
    img: IMG.custom,
    items: ["DTF", "Bordado"],
    num: "03",
  },
];

const Services = () => (
  <section id="servicios" className="bg-[#0A0A0A] py-24 sm:py-36 scroll-mt-16" data-testid="services">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-16 gap-6">
        <FadeUp>
          <span className="patch text-[var(--fuchsia)] mb-5 -rotate-2">02 / Qué hacemos</span>
          <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-6xl lg:text-7xl mt-5">
            Servicios
          </h2>
        </FadeUp>
        <FadeUp delay={0.1}>
          <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-500 max-w-xs">
            Productos y técnicas para vestir tu marca de punta a punta.
          </p>
        </FadeUp>
      </div>

      <div className="grid md:grid-cols-3 gap-5">
        {SERVICES.map((s, i) => (
          <FadeUp key={s.tag} delay={i * 0.1}>
            <motion.div
              whileHover={{ y: -8 }}
              transition={{ duration: 0.4, ease }}
              className="group border border-white/10 bg-[#0d0d0d] overflow-hidden h-full flex flex-col"
              data-testid={`service-card-${i}`}
            >
              <div className="relative aspect-[4/5] overflow-hidden">
                <motion.img
                  src={s.img}
                  alt={s.tag}
                  className="w-full h-full object-cover"
                  whileHover={{ scale: 1.06 }}
                  transition={{ duration: 0.6, ease }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-transparent to-transparent" />
                <span className="absolute top-4 left-4 font-display text-5xl text-white/80">{s.num}</span>
              </div>
              <div className="p-6 flex flex-col gap-4 flex-1">
                <h3 className="font-display uppercase text-2xl tracking-tight group-hover:text-[var(--yellow)] transition-colors">
                  {s.tag}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {s.items.map((it) => (
                    <span key={it} className="patch text-neutral-400 rotate-[-1deg]">{it}</span>
                  ))}
                </div>
              </div>
            </motion.div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Process ---------- */
const STEPS = [
  "Nos cuentas tu idea",
  "Te asesoramos",
  "Diseñamos la mejor propuesta",
  "Producimos con materiales de calidad",
  "Entregamos productos listos para representar tu marca",
];

const Process = () => (
  <section id="proceso" className="bg-[#0A0A0A] border-t border-white/10 py-24 sm:py-36 scroll-mt-16" data-testid="process">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <FadeUp>
        <span className="patch text-[var(--yellow)] -rotate-2">03 / Metodología</span>
        <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-6xl lg:text-7xl mt-5 mb-16">
          Nuestro proceso
        </h2>
      </FadeUp>

      <div className="grid md:grid-cols-5 gap-0 border-t border-white/10">
        {STEPS.map((step, i) => (
          <FadeUp key={i} delay={i * 0.08}>
            <div
              className="group relative p-6 border-b md:border-b-0 md:border-r border-white/10 h-full flex flex-col gap-6 hover:bg-white/[0.03] transition-colors"
              data-testid={`process-step-${i}`}
            >
              <div className="flex items-baseline gap-3">
                <span className="font-display text-6xl text-white/15 group-hover:text-[var(--fuchsia)] transition-colors">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <p className="text-base text-neutral-300 leading-snug">{step}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Projects ---------- */
const Projects = () => (
  <section id="proyectos" className="bg-[var(--bg-light)] text-black py-24 sm:py-36 scroll-mt-16" data-testid="projects">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <FadeUp>
        <span className="patch text-black -rotate-2">04 / Caso real</span>
        <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-6xl lg:text-7xl mt-5 mb-14">
          Proyectos realizados
        </h2>
      </FadeUp>

      <div className="grid lg:grid-cols-2 gap-10 items-center">
        <FadeUp>
          <div className="relative border-2 border-black overflow-hidden" data-testid="project-image">
            <img src={IMG.project} alt="Proyecto Círculo de Suboficiales" className="w-full h-full object-cover aspect-[4/3]" />
          </div>
        </FadeUp>
        <FadeUp delay={0.15}>
          <div className="flex flex-col gap-8">
            <div>
              <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">Cliente</p>
              <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-full overflow-hidden shrink-0" data-testid="client-logo">
                <img src={IMG.clientPNA} alt="Círculo de Suboficiales de la Prefectura Naval Argentina" className="w-full h-full object-cover scale-110" />
              </div>
            </div>
            <div className="border-t border-black/20 pt-6">
              <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Trabajo</p>
              <p className="text-lg">Desarrollo de chombas, delantales de cocina y tazas.</p>
            </div>
            <div className="border-t border-black/20 pt-6">
              <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-500 mb-2">Resultado</p>
              <p className="text-lg">Mayor coherencia visual en eventos y visitas comerciales.</p>
            </div>
          </div>
        </FadeUp>
      </div>

      {/* Clientes */}
      <FadeUp>
        <div className="mt-20 sm:mt-28 pt-12 border-t border-black/15">
          <p className="font-mono-x text-xs uppercase tracking-[0.25em] text-neutral-500 mb-8">Confían en nosotros</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="clients-grid">
            <div className="bg-[#0A0A0A] rounded-sm aspect-[3/2] flex items-center justify-center p-6 sm:p-8" data-testid="client-tccar">
              <img src={IMG.clientTCCAR} alt="TCCAR — Estética Vehicular" className="max-w-full max-h-full object-contain" />
            </div>
            <div className="bg-[#0A0A0A] rounded-sm aspect-[3/2] flex items-center justify-center p-6 sm:p-8" data-testid="client-pna">
              <img src={IMG.clientPNA} alt="Círculo de Suboficiales de la Prefectura Naval Argentina" className="max-w-full max-h-full object-contain" />
            </div>
          </div>
        </div>
      </FadeUp>
    </div>
  </section>
);
const WHY = [
  { icon: ShieldCheck, title: "Calidad", desc: "Materiales que representan tu marca." },
  { icon: Lightbulb, title: "Asesoría en branding", desc: "No solo imprimimos un logo, te ayudamos a tomar las mejores decisiones." },
  { icon: Layers, title: "Producción", desc: "DTF y bordado." },
  { icon: HeartHandshake, title: "Atención personalizada", desc: "Cada empresa tiene una identidad distinta, cada proyecto también." },
];

const WhyUs = () => (
  <section className="bg-[#0A0A0A] py-24 sm:py-36" data-testid="why-us">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
      <FadeUp>
        <span className="patch text-[var(--fuchsia)] -rotate-2">05 / Diferencial</span>
        <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-6xl lg:text-7xl mt-5 mb-16">
          ¿Por qué Distópica?
        </h2>
      </FadeUp>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {WHY.map((w, i) => (
          <FadeUp key={w.title} delay={i * 0.08}>
            <div
              className="group border border-white/10 p-7 h-full flex flex-col gap-5 hover:border-[var(--yellow)]/50 transition-colors"
              data-testid={`why-card-${i}`}
            >
              <div className="w-12 h-12 border border-white/20 flex items-center justify-center rounded-sm group-hover:bg-[var(--yellow)] group-hover:text-black group-hover:border-[var(--yellow)] transition-colors">
                <w.icon size={22} strokeWidth={1.6} />
              </div>
              <h3 className="font-display uppercase text-xl tracking-tight">{w.title}</h3>
              <p className="text-sm text-neutral-400 leading-relaxed">{w.desc}</p>
            </div>
          </FadeUp>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Contact ---------- */
const Contact = () => {
  const [form, setForm] = useState({ nombre: "", empresa: "", telefono: "", email: "", mensaje: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [errors, setErrors] = useState({});

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const err = {};
    if (!form.nombre.trim()) err.nombre = "El nombre es obligatorio";
    if (!form.email.trim()) err.email = "El email es obligatorio";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) err.email = "Email inválido";
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("¡Consulta enviada! Te contactaremos pronto.");
      setForm({ nombre: "", empresa: "", telefono: "", email: "", mensaje: "" });
    } catch (err) {
      toast.error("Hubo un problema al enviar. Intentá nuevamente.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-4 text-lg placeholder:text-neutral-600 focus:border-[var(--yellow)] focus:outline-none transition-colors";

  return (
    <section id="contacto" className="bg-[#0A0A0A] border-t border-white/10 py-24 sm:py-36 scroll-mt-16" data-testid="contact">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-16">
        <div>
          <FadeUp>
            <span className="patch text-[var(--yellow)] -rotate-2">06 / Hablemos</span>
            <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-6xl mt-5">
              Tu marca merece mucho más que un logo estampado
            </h2>
            <p className="mt-6 text-lg text-neutral-300 max-w-md">
              Conversemos sobre tu próximo proyecto.
            </p>
          </FadeUp>

          <FadeUp delay={0.15}>
            <div className="mt-12 flex flex-col gap-4">
              <a href="tel:+541159973445" data-testid="contact-phone" className="flex items-center gap-3 text-neutral-300 hover:text-[var(--yellow)] transition-colors">
                <Phone size={18} /> <span className="font-mono-x text-sm">11 5997-3445</span>
              </a>
              <a href="https://instagram.com/distopica.tienda" target="_blank" rel="noreferrer" data-testid="contact-instagram" className="flex items-center gap-3 text-neutral-300 hover:text-[var(--yellow)] transition-colors">
                <Instagram size={18} /> <span className="font-mono-x text-sm">@distopica.tienda</span>
              </a>
              <a href="mailto:distopicadisenos@gmail.com" data-testid="contact-email" className="flex items-center gap-3 text-neutral-300 hover:text-[var(--yellow)] transition-colors">
                <Mail size={18} /> <span className="font-mono-x text-sm">distopicadisenos@gmail.com</span>
              </a>
            </div>
          </FadeUp>
        </div>

        <FadeUp delay={0.1}>
          {sent ? (
            <div className="h-full flex flex-col items-start justify-center border border-[var(--yellow)]/40 p-10" data-testid="contact-success">
              <div className="w-14 h-14 rounded-full bg-[var(--yellow)] text-black flex items-center justify-center mb-6">
                <Check size={28} />
              </div>
              <h3 className="font-display uppercase text-3xl mb-3">¡Gracias!</h3>
              <p className="text-neutral-300 mb-8">
                Recibimos tu consulta y te vamos a contactar a la brevedad.
              </p>
              <button
                onClick={() => setSent(false)}
                data-testid="contact-reset"
                className="font-mono-x text-xs uppercase tracking-[0.2em] border border-white/30 px-6 py-3 rounded-sm hover:border-[var(--yellow)] hover:text-[var(--yellow)] transition-colors"
              >
                Enviar otra consulta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="flex flex-col gap-7" data-testid="contact-form" noValidate>
              <div>
                <input
                  data-testid="input-nombre"
                  className={inputCls}
                  placeholder="Nombre *"
                  value={form.nombre}
                  onChange={set("nombre")}
                />
                {errors.nombre && <p className="text-[var(--fuchsia)] text-xs mt-2 font-mono-x" data-testid="error-nombre">{errors.nombre}</p>}
              </div>
              <div>
                <input
                  data-testid="input-empresa"
                  className={inputCls}
                  placeholder="Empresa"
                  value={form.empresa}
                  onChange={set("empresa")}
                />
              </div>
              <div>
                <input
                  data-testid="input-telefono"
                  className={inputCls}
                  type="tel"
                  placeholder="Teléfono"
                  value={form.telefono}
                  onChange={set("telefono")}
                />
              </div>
              <div>
                <input
                  data-testid="input-email"
                  className={inputCls}
                  placeholder="Email *"
                  value={form.email}
                  onChange={set("email")}
                />
                {errors.email && <p className="text-[var(--fuchsia)] text-xs mt-2 font-mono-x" data-testid="error-email">{errors.email}</p>}
              </div>
              <div>
                <textarea
                  data-testid="input-mensaje"
                  className={`${inputCls} resize-none`}
                  placeholder="Contanos tu proyecto"
                  rows={4}
                  value={form.mensaje}
                  onChange={set("mensaje")}
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                data-testid="submit-button"
                className="group inline-flex items-center justify-center gap-2 bg-[var(--fuchsia)] text-white font-mono-x uppercase text-sm tracking-[0.15em] px-7 py-4 rounded-sm hover:bg-[var(--yellow)] hover:text-black transition-colors disabled:opacity-60"
              >
                {sending ? "Enviando..." : "Solicitar cotización"}
                <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
              </button>
            </form>
          )}
        </FadeUp>
      </div>
    </section>
  );
};

/* ---------- Blog teaser ---------- */
const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("es-AR", { day: "2-digit", month: "long", year: "numeric" });

const BlogTeaser = () => {
  const [posts, setPosts] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get("/blog/posts").then((r) => setPosts(r.data.slice(0, 3))).catch(() => setPosts([]));
  }, []);

  if (posts.length === 0) return null;

  return (
    <section id="blog" className="bg-[#0A0A0A] border-t border-white/10 py-24 sm:py-36 scroll-mt-16" data-testid="blog-teaser">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-16 gap-6">
          <FadeUp>
            <span className="patch text-[var(--yellow)] -rotate-2">07 / Recursos</span>
            <h2 className="font-display uppercase leading-[0.9] tracking-tight text-4xl sm:text-6xl lg:text-7xl mt-5">
              Desde el blog
            </h2>
          </FadeUp>
          <FadeUp delay={0.1}>
            <button
              onClick={() => navigate("/blog")}
              data-testid="blog-view-all"
              className="group inline-flex items-center gap-2 font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-[var(--yellow)] transition-colors"
            >
              Ver todos los artículos
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </button>
          </FadeUp>
        </div>

        <div className="grid md:grid-cols-3 gap-5">
          {posts.map((p, i) => (
            <FadeUp key={p.slug} delay={i * 0.1}>
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`teaser-card-${i}`}
                className="group block border border-white/10 h-full hover:border-[var(--yellow)]/50 transition-colors"
              >
                <div className="aspect-[16/10] overflow-hidden">
                  <img src={resolveImg(p.cover_image)} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex flex-col gap-3">
                  <span className="font-mono-x text-[11px] uppercase tracking-widest text-neutral-500">{fmtDate(p.created_at)}</span>
                  <h3 className="font-display uppercase text-xl leading-tight tracking-tight group-hover:text-[var(--yellow)] transition-colors">{p.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed line-clamp-2">{p.excerpt}</p>
                </div>
              </Link>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
const Footer = () => (
  <footer className="bg-[#0A0A0A] border-t border-white/10 py-10" data-testid="footer">
    <div className="max-w-[1400px] mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
      <span className="font-display text-xl tracking-tight">DISTÓPICA<span style={{ color: "var(--fuchsia)" }}>.</span></span>
      <div className="flex items-center gap-6">
        <Link to="/blog" className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-400 hover:text-[var(--yellow)] transition-colors" data-testid="footer-blog">Blog</Link>
        <p className="font-mono-x text-xs uppercase tracking-[0.2em] text-neutral-500">
          Indumentaria & Merch corporativo — DTF & Bordado
        </p>
      </div>
    </div>
  </footer>
);

/* ---------- Page ---------- */
export default function Landing() {
  useEffect(() => {
    if (window.location.hash) {
      const id = window.location.hash.slice(1);
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 300);
    }
  }, []);
  return (
    <main>
      <Toaster position="top-center" theme="dark" richColors />
      <Nav />
      <Hero />
      <Ribbon />
      <Manifesto />
      <Services />
      <Process />
      <Projects />
      <WhyUs />
      <BlogTeaser />
      <Contact />
      <Footer />
    </main>
  );
}
